import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Protocol, ProtocolAttachmentFilter, ProtocolAuditFilter, ProtocolFilter } from '../model/protocol';

import { HttpUtils } from '../utils/httpUtils';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ProtocolReportFilter } from '../model/attachment';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';

@Injectable()
export class ProtocolService {

    constructor(private http: HttpClient) {
    }

    findAll(filter: ProtocolFilter): Observable<any> {
        const url = environment.thermalUrl + '/protocol/';

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getExcel(filter: ProtocolFilter): Observable<any> {
        const url = environment.thermalUrl + '/protocol/excel';

        return this.http.post(url, filter, { responseType: 'blob' });
    }

    findAllAssignable(filter: ProtocolFilter): Observable<any> {
        const url = environment.thermalUrl + '/protocol/assignable';

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    findOne(id: number): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${id}`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getAudit(id: number, filter: ProtocolAuditFilter): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${id}/audit`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    save(protocol: Protocol, token: string): Observable<any> {
        return this.genericSave(protocol, token, null);
    }

    signInfo(prot: Protocol): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${prot.id}/signInfo`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    addSign(prot: Protocol, token: string, signTypes: number[], user: string, lang: string): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${prot.id}/addSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('user', user);
        form.append('signTypes', signTypes.join(';'));
        form.append('lang', lang);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    refuseToSign(prot: Protocol, token: string, reason: string): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${prot.id}/refuseToSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('reason', reason);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    manualSign(prot: Protocol, file: File, token: string): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${prot.id}/manualSign`;

        const form = new FormData();
        form.append('protocol', JSON.stringify(prot));
        form.append('token', token);
        form.append('document', file, file.name);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    automaticSign(prot: Protocol, token: string, signTypes: number[], lang: string): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${prot.id}/automaticSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('signTypes', signTypes.join(';'));
        form.append('lang', lang);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    delete(id: number): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${id}`;

        return this.http.delete(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadPdf(id: number, config: ProtocolReportFilter = new ProtocolReportFilter()): Observable<any> {
        const url = environment.thermalUrl + `/report/protocol/${id}`;

        const headers = new HttpHeaders({
            lang: config.lang
        });

        const data = {
            reason: config.reason,
            reviewBy: config.reviewBy,
            toSign: config.toSign,
            hybride: config.hybride,
            signature: config.isSignature
        };

        return this.http.post(url, data, { responseType: 'blob', headers });
    }

    downloadPdfProtocolVersion(protocol: Protocol): Observable<any> {
        return this.downloadPdfVersion(protocol.id, protocol.signedVersion);
    }

    downloadPdfVersion(idProtocol: number, version: number): Observable<any> {
        const url = environment.thermalUrl + `/report/protocol/${idProtocol}/version/${version}`;

        return this.http.post(url, {}, { responseType: 'blob' });
    }

    downloadAuditPdf(id: number): Observable<any> {
        const url = environment.thermalUrl + `/report/protocol/${id}/audit`;

        return this.http.get(url, { responseType: 'blob' });
    }

    getAttachments(id: number, filter: ProtocolAttachmentFilter): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${id}/attachment`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    moveAttachment(id: number, previousIndex: number, currentIndex: number): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${id}/attachment/move`;

        return this.http.post(url, { previousIndex, currentIndex }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    revertSign(idProtocol: number, reason: string, token: string): Observable<any> {
        const url = environment.thermalUrl + `/protocol/${idProtocol}/revertSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('reason', reason);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    checkCopyToGroup(id: number, idGroup: number): Observable<any> {
        const url = environment.thermalUrl + `/protocol/clonate/${id}/toGroup/${idGroup}/check`;

        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyToGroup(id: number, idGroup: number): Observable<any> {
        const url = environment.thermalUrl + `/protocol/clonate/${id}/toGroup/${idGroup}`;

        return this.http.put(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    generateReportSignatureTemp(idProtocol: number, version: number) {
        const url = `${environment.thermalUrl}/report/protocol/${idProtocol}/${version}/temporalSignature`;
        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    private genericSave(protocol: Protocol, token: string, action?: string): Observable<any> {
        let url = environment.thermalUrl + '/protocol/';

        if (protocol.id != null) {
            url += protocol.id;

            if (action != null) {
                url += action;
            }

            return this.http.put(url, { protocol, token }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            url += 'create';

            return this.http.post(url, { protocol, token }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        }
    }
}
